import { useState, useEffect, useMemo } from 'react';
import { Paper, Box, Typography, Stack, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { FileDownloadOutlined, RefreshOutlined } from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useGetCaseCategoryAnalyticsRange } from '../../../../../../../../features/analytics/hooks/useGetCaseCategoryAnalytics';
import { useExportCaseCategoryAnalytics } from '../../../../../../../../features/analytics/hooks/exports';
import FullScreenLoader from '../../../../../../caller/components/fullscreen-loader';
import { categoryBorderColors, categoryColors, categoryColorsDelivered, categoryColorsNewRegistration, dataLabelsCategoryDefault, dataLabelsDefault } from '../../utility/graph';
import dayjs from 'dayjs';
import DateRangePicker from '../../../../components/date-range-picker';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export const categories = [
  "Allocated",
  "Attempted",
  "Connected",
  "Unreached",
  "Child Above 2 Years",
  "Not in the portal",
  "Pending",
  "Active",
  "Unattempted"
];

// export const categoriesMap = {
//   "Allocated": "allocated",
//   "Attempted": "attempted",
//   "Connected": "connected",
//   "Unreached": "unreached",
//   "Child Above 2 Years": "childAbove2Years",
//   "Not in portal": "notInPortal",
//   "Pending": "pending",
//   "Active": "active",
//   "Unattempted": "unattempted"
// };

export const categoriesMap = new Map(
  [
    ["Allocated", "allocated"],
    ["Attempted", "attempted"],
    ["Connected", "connected"],
    ["Unreached", "unreached"],
    ["Child Above 2 Years", "childAbove2Years"],
    ["Not in the portal", "notInPortal"],
    ["Pending", "pending"],
    ["Active", "active"],
    ["Unattempted", "unattempted"]
  ]
)
//   "Allocated": "allocated",
//   "Attempted": "attempted",
//   "Connected": "connected",
//   "Unreached": "unreached",
//   "Child Above 2 Years": "childAbove2Years",
//   "Not in portal": "notInPortal",
//   "Pending": "pending",
//   "Active": "active",
//   "Unattempted": "unattempted"
// };

export const CategoryAnalyticsChart = ({ fromDate: controlledFromDate, toDate: controlledToDate, onFromDateChange, onToDateChange }) => {
  const [localFromDate, setLocalFromDate] = useState(dayjs().startOf('month'));
  const [localToDate, setLocalToDate] = useState(dayjs().endOf('month'));
  const [viewMode, setViewMode] = useState('count'); // 'count' or 'percentage'
  const fromDate = controlledFromDate !== undefined ? controlledFromDate : localFromDate;
  const toDate = controlledToDate !== undefined ? controlledToDate : localToDate;

  useEffect(() => {
    if (controlledFromDate !== undefined) {
      setLocalFromDate(controlledFromDate);
    }
  }, [controlledFromDate]);

  useEffect(() => {
    if (controlledToDate !== undefined) {
      setLocalToDate(controlledToDate);
    }
  }, [controlledToDate]);

  const handleFromDateChange = (newDate) => {
    setLocalFromDate(newDate);
  };

  const handleToDateChange = (newDate) => {
    setLocalToDate(newDate);
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  // Always refetch on mount by using a unique key (forces remount/fetch)
  const [fetchKey] = useState(() => Date.now());
  // We will refetch date whenever the local date changes
  // Because we are updating the local dates when the controlled dates change
  const { data: categoryAnalytics, isFetching, refetch } = useGetCaseCategoryAnalyticsRange(
    localFromDate,
    localToDate,
    fetchKey
  );

  // Export hook with UX callbacks
  const exportCaseCategoryAnalytics = useExportCaseCategoryAnalytics({
    onSuccess: (data) => {
      toast.success(`Category analytics exported successfully! File: ${data.filename}`);
    },
    onError: (error) => {
      toast.error(`Export failed: ${error.message}`);
    }
  });

  // Handle export button click
  const handleExport = () => {
    const exportParams = {
      from: fromDate.format('YYYY-MM-DD'),
      to: toDate.format('YYYY-MM-DD'),
    };

    toast.loading('Preparing export...', { id: 'category-analytics-export' });

    exportCaseCategoryAnalytics.mutate(exportParams, {
      onSettled: () => {
        toast.dismiss('category-analytics-export');
      }
    });
  };

  // Handle refresh button click
  const handleRefresh = () => {
    refetch();
  };

  console.log(`[CategoryAnalyticsChart] Backend Data:`, categoryAnalytics);

  // Get category analytics data for selected date range
  const getCategoryData = () => {
    const categoryAnalyticsData = categoryAnalytics?.data || {};

    // Initialize data structure
    const categories = Array.from(categoriesMap.keys());

    const categoryData = {
      recentlyDelivered: {},
      newRegistration: {},
      recentlyDeliveredPercentage: {},
      newRegistrationPercentage: {}
    };

    let totalRecentlyDelivered = 0;
    let totalNewRegistration = 0;

    categories.forEach(category => {
      const key = categoriesMap.get(category);

      // Count data
      categoryData.recentlyDelivered[key] = categoryAnalyticsData[key]?.recentlyDelivered || 0;
      categoryData.newRegistration[key] = categoryAnalyticsData[key]?.newRegistration || 0;

      // Percentage data
      categoryData.recentlyDeliveredPercentage[key] = categoryAnalyticsData[key]?.recentlyDeliveredPercentage || 0;
      categoryData.newRegistrationPercentage[key] = categoryAnalyticsData[key]?.newRegistrationPercentage || 0;
    });

    if (categoryAnalyticsData) {
      totalNewRegistration = categoryAnalyticsData.totalNewRegistration || 0;
      totalRecentlyDelivered = categoryAnalyticsData.totalRecentlyDelivered || 0;
    }

    const isPercentageView = viewMode === 'percentage';
    const dataLabel = isPercentageView ? 'Percentage' : 'Count';

    return {
      chartData: {
        labels: categories,
        datasets: [
          {
            label: `New Registration ${dataLabel}`,
            data: isPercentageView
              ? Object.values(categoryData.newRegistrationPercentage)
              : Object.values(categoryData.newRegistration),
            backgroundColor: Object.values(categoryColorsNewRegistration).map(color =>
              color.replace('0.8', '0.6')
            ),
            borderColor: Object.values(categoryColorsNewRegistration),
            borderWidth: 1,
          },
          {
            label: `Recently Delivered ${dataLabel}`,
            data: isPercentageView
              ? Object.values(categoryData.recentlyDeliveredPercentage)
              : Object.values(categoryData.recentlyDelivered),
            backgroundColor: Object.values(categoryColorsDelivered),
            borderColor: Object.values(categoryColorsDelivered),
            borderWidth: 1,
          }
        ]
      },
      totals: {
        recentlyDelivered: totalRecentlyDelivered,
        newRegistration: totalNewRegistration
      }
    };
  };

  const categoryChartOptions = {
    indexAxis: 'y',
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        display: false,
        labels: {
          usePointStyle: false,
          // pointStyle: 'rect',
          padding: 20,
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#333',
        }
      },
      title: {
        display: true,
        text: `Category Analytics - ${fromDate.format('MMM YYYY')} to ${toDate.format('MMM YYYY')} (${viewMode === 'percentage' ? 'Percentage' : 'Count'})`,
        font: {
          size: 16
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || '';
            const value = context.parsed.x;
            return viewMode === 'percentage'
              ? `${label}: ${value}%`
              : `${label}: ${value}`;
          }
        }
      },
      datalabels: dataLabelsCategoryDefault
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: {
          display: true,
        },
        ticks: {
          stepSize: viewMode === 'percentage' ? 10 : 50,
          callback: function (value) {
            return viewMode === 'percentage' ? `${value}%` : value;
          }
        }
      },
      y: {
        grid: {
          display: false,
        }
      }
    },
    maintainAspectRatio: false,
  };

  const categoryData = getCategoryData();

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2, border: '1px solid #d0d0d0' }}>
      <FullScreenLoader open={isFetching} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <DateRangePicker
          fromDate={fromDate}
          toDate={toDate}
          onFromChange={handleFromDateChange}
          onToChange={handleToDateChange}
          disabled={isFetching || exportCaseCategoryAnalytics.isLoading}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          disabled={isFetching || exportCaseCategoryAnalytics.isLoading}
        >
          <ToggleButton value="count">
            Count
          </ToggleButton>
          <ToggleButton value="percentage">
            Percentage
          </ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={handleRefresh}
            disabled={isFetching || exportCaseCategoryAnalytics.isLoading}
            size="small"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#f3f4f6',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlined />}
            onClick={handleExport}
            disabled={isFetching || exportCaseCategoryAnalytics.isLoading || !categoryAnalytics?.data}
            size="small"
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: '#f3f4f6',
              },
              '&:disabled': {
                borderColor: '#e0e0e0',
                color: '#9e9e9e',
              }
            }}
          >
            {exportCaseCategoryAnalytics.isLoading ? 'Exporting...' : 'Export'}
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ height: 500, width: '100%' }}>
        {!isFetching && <Bar data={categoryData.chartData} options={categoryChartOptions} />}
      </Box>

      {/* Legend with counts */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 4 }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#666' }}>
          Recently Delivered: {categoryData.totals.recentlyDelivered}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#666' }}>
          New Registration: {categoryData.totals.newRegistration}
        </Typography>
      </Box>
    </Paper>
  );
};
